import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import AuthContext from "../../hooks/authContext";
import useOnboardingProgress from "../../hooks/useOnboardingProgress";
import {
  consumeOnboardingWidgetResetOnLogin,
  readOnboardingFloatingDismissed,
  readOnboardingModalDismissed,
  readOnboardingSidebarDismissed,
  writeOnboardingFloatingDismissed,
  writeOnboardingModalDismissed,
  writeOnboardingSidebarDismissed,
} from "../../utils/onboardingWidgetDismiss";
import {
  getCompanySizeValue,
  isCompanySizeReady,
  isLargeCompanyByEmployeeCount,
  shouldShowOnboardingCallOption,
} from "../../utils/companyEmployeeCount";

const DEFAULT_ONBOARDING_EXPERIENCE = {
  loading: false,
  progress: {
    profileComplete: false,
    clientAdded: false,
    buildingAdded: false,
    crewAdded: false,
    shiftScheduled: false,
  },
  completedSteps: 0,
  totalSteps: 4,
  modalCompletedSteps: 0,
  modalTotalSteps: 4,
  modalProgressPercentage: 0,
  isComplete: false,
  hasShiftsBooked: false,
  isCompanyUser: false,
  userData: null,
  optionalProfileFieldsMissing: {
    companyAddress: false,
    companyZip: false,
  },
  optionalProfileFieldLabels: [],
  hasOptionalProfileFieldsMissing: false,
  isModalOpen: false,
  isDecisionModalOpen: false,
  showOnboardingCallCta: false,
  usesLargeCompanyOnboarding: false,
  openModal: () => {},
  openChecklistModal: () => {},
  closeModal: () => {},
  minimizeModal: () => {},
  minimizeDecisionModal: () => {},
  dismissForSession: () => {},
  dismissFloatingWidget: () => {},
  dismissSidebarIndicator: () => {},
  isFloatingWidgetDismissed: false,
  isSidebarIndicatorDismissed: false,
  allowCompactOnboarding: false,
  shouldShowIndicator: false,
  showCompletionIndicator: false,
};

const OnboardingExperienceContext = createContext(DEFAULT_ONBOARDING_EXPERIENCE);

export const OnboardingExperienceProvider = ({ children }) => {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const isCompanyUser = auth?.userProfile?.role === "companyUser";
  const userId = isCompanyUser ? auth?.userProfile?.id : null;
  const onboarding = useOnboardingProgress(userId);
  const sizeProfile = onboarding.userData || auth?.userProfile;
  const companySizeValue = getCompanySizeValue(sizeProfile);
  const isCompanySizeResolved = isCompanySizeReady(sizeProfile, {
    loading: onboarding.loading,
    hasUserData: Boolean(onboarding.userData),
  });
  const usesLargeCompanyOnboarding = isLargeCompanyByEmployeeCount(companySizeValue);
  const showOnboardingCallCta = shouldShowOnboardingCallOption(sizeProfile);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
  const [showCompletionIndicator, setShowCompletionIndicator] = useState(false);
  const [isFloatingWidgetDismissed, setIsFloatingWidgetDismissed] = useState(false);
  const [isSidebarIndicatorDismissed, setIsSidebarIndicatorDismissed] = useState(false);
  const [allowCompactOnboarding, setAllowCompactOnboarding] = useState(false);
  const initialCompletionRef = useRef(null);
  const autoOpenTimeoutRef = useRef(null);
  const initializedUserIdRef = useRef(null);
  const manualModalDismissRef = useRef(false);
  const openedChecklistIntentionallyRef = useRef(false);
  const usesLargeCompanyOnboardingRef = useRef(usesLargeCompanyOnboarding);

  usesLargeCompanyOnboardingRef.current = usesLargeCompanyOnboarding;

  const clearAutoOpenTimeout = useCallback(() => {
    if (autoOpenTimeoutRef.current) {
      clearTimeout(autoOpenTimeoutRef.current);
      autoOpenTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      initializedUserIdRef.current = null;
      manualModalDismissRef.current = false;
      setIsFloatingWidgetDismissed(false);
      setIsSidebarIndicatorDismissed(false);
      setAllowCompactOnboarding(false);
      clearAutoOpenTimeout();
      setIsModalOpen(false);
      setIsDecisionModalOpen(false);
      setShowCompletionIndicator(false);
      initialCompletionRef.current = null;
      return;
    }

    if (initializedUserIdRef.current === userId) {
      return;
    }

    initializedUserIdRef.current = userId;
    manualModalDismissRef.current = false;
    openedChecklistIntentionallyRef.current = false;

    if (consumeOnboardingWidgetResetOnLogin(userId)) {
      setIsFloatingWidgetDismissed(false);
      setIsSidebarIndicatorDismissed(false);
      setAllowCompactOnboarding(false);
    } else {
      setIsFloatingWidgetDismissed(readOnboardingFloatingDismissed(userId));
      setIsSidebarIndicatorDismissed(readOnboardingSidebarDismissed(userId));
      setAllowCompactOnboarding(readOnboardingModalDismissed(userId));
    }

    clearAutoOpenTimeout();
    setIsModalOpen(false);
    setIsDecisionModalOpen(false);
    setShowCompletionIndicator(false);
    initialCompletionRef.current = null;
  }, [clearAutoOpenTimeout, userId]);

  const revealCompactOnboarding = useCallback(() => {
    manualModalDismissRef.current = true;
    setIsModalOpen(false);
    setIsDecisionModalOpen(false);
    setIsFloatingWidgetDismissed(false);
    setAllowCompactOnboarding(true);
  }, []);

  const dismissForSession = useCallback(() => {
    clearAutoOpenTimeout();
    writeOnboardingModalDismissed(userId);
    revealCompactOnboarding();
  }, [clearAutoOpenTimeout, revealCompactOnboarding, userId]);

  const minimizeModal = useCallback(() => {
    clearAutoOpenTimeout();
    revealCompactOnboarding();
  }, [clearAutoOpenTimeout, revealCompactOnboarding]);

  const dismissFloatingWidget = useCallback(() => {
    writeOnboardingFloatingDismissed(userId);
    setIsFloatingWidgetDismissed(true);
  }, [userId]);

  const dismissSidebarIndicator = useCallback(() => {
    writeOnboardingSidebarDismissed(userId);
    setIsSidebarIndicatorDismissed(true);
  }, [userId]);

  const openModal = useCallback(() => {
    clearAutoOpenTimeout();
    openedChecklistIntentionallyRef.current = false;
    setIsModalOpen(false);
    if (usesLargeCompanyOnboardingRef.current) {
      setIsDecisionModalOpen(true);
      return;
    }
    setIsDecisionModalOpen(false);
    setIsModalOpen(true);
  }, [clearAutoOpenTimeout]);

  const openChecklistModal = useCallback(() => {
    clearAutoOpenTimeout();
    openedChecklistIntentionallyRef.current = true;
    setIsDecisionModalOpen(false);
    setIsModalOpen(true);
  }, [clearAutoOpenTimeout]);

  const minimizeDecisionModal = useCallback(() => {
    clearAutoOpenTimeout();
    revealCompactOnboarding();
  }, [clearAutoOpenTimeout, revealCompactOnboarding]);

  const closeModal = useCallback(() => {
    minimizeModal();
  }, [minimizeModal]);

  const scheduleAutoOpen = useCallback(() => {
    clearAutoOpenTimeout();
    autoOpenTimeoutRef.current = setTimeout(() => {
      autoOpenTimeoutRef.current = null;

      if (manualModalDismissRef.current || readOnboardingModalDismissed(userId)) {
        return;
      }

      if (usesLargeCompanyOnboardingRef.current) {
        setIsDecisionModalOpen(true);
      } else {
        setIsModalOpen(true);
      }
    }, 450);
  }, [clearAutoOpenTimeout, userId]);

  useEffect(() => {
    if (
      usesLargeCompanyOnboarding &&
      isModalOpen &&
      !openedChecklistIntentionallyRef.current &&
      !allowCompactOnboarding
    ) {
      setIsModalOpen(false);
      setIsDecisionModalOpen(true);
    }
  }, [allowCompactOnboarding, isModalOpen, usesLargeCompanyOnboarding]);

  useEffect(() => {
    if (!userId || onboarding.loading || !onboarding.isCompanyUser || !isCompanySizeResolved) {
      clearAutoOpenTimeout();
      return;
    }

    if (initialCompletionRef.current === true && !onboarding.isComplete) {
      initialCompletionRef.current = false;
      setShowCompletionIndicator(false);
    }

    if (initialCompletionRef.current === null) {
      initialCompletionRef.current = onboarding.isComplete;

      if (onboarding.isComplete) {
        clearAutoOpenTimeout();
        setShowCompletionIndicator(false);
        return;
      }

      setShowCompletionIndicator(false);
    }

    const isDashboardRoute = location?.pathname === "/" || location?.pathname === "/dashboard";
    const modalDismissedForSession = readOnboardingModalDismissed(userId);
    const shouldAutoOpen =
      !onboarding.isComplete &&
      !modalDismissedForSession &&
      !manualModalDismissRef.current &&
      !isModalOpen &&
      !isDecisionModalOpen &&
      !allowCompactOnboarding &&
      isDashboardRoute;

    if (shouldAutoOpen) {
      scheduleAutoOpen();
      return;
    }

    clearAutoOpenTimeout();

    if (!initialCompletionRef.current && onboarding.isComplete) {
      setIsModalOpen(false);
      setIsDecisionModalOpen(false);
      setAllowCompactOnboarding(true);
      setShowCompletionIndicator(true);
      return;
    }

    if (!onboarding.isComplete) {
      setShowCompletionIndicator(false);
    }
  }, [
    allowCompactOnboarding,
    clearAutoOpenTimeout,
    isDecisionModalOpen,
    isModalOpen,
    onboarding.isCompanyUser,
    onboarding.isComplete,
    onboarding.loading,
    scheduleAutoOpen,
    userId,
    location?.pathname,
    companySizeValue,
    isCompanySizeResolved,
  ]);

  useEffect(() => () => clearAutoOpenTimeout(), [clearAutoOpenTimeout]);

  const shouldShowIndicator = useMemo(() => {
    if (!userId || onboarding.loading || !onboarding.isCompanyUser) {
      return false;
    }

    return !onboarding.isComplete || showCompletionIndicator;
  }, [
    onboarding.isCompanyUser,
    onboarding.isComplete,
    onboarding.loading,
    showCompletionIndicator,
    userId,
  ]);

  const value = useMemo(
    () => ({
      ...onboarding,
      hasShiftsBooked: onboarding.progress.shiftScheduled,
      isModalOpen,
      isDecisionModalOpen,
      showOnboardingCallCta,
      usesLargeCompanyOnboarding,
      openModal,
      openChecklistModal,
      closeModal,
      minimizeModal,
      minimizeDecisionModal,
      dismissForSession,
      dismissFloatingWidget,
      dismissSidebarIndicator,
      isFloatingWidgetDismissed,
      isSidebarIndicatorDismissed,
      allowCompactOnboarding,
      shouldShowIndicator,
      showCompletionIndicator,
    }),
    [
      dismissFloatingWidget,
      dismissSidebarIndicator,
      dismissForSession,
      isFloatingWidgetDismissed,
      isSidebarIndicatorDismissed,
      allowCompactOnboarding,
      isDecisionModalOpen,
      isModalOpen,
      onboarding,
      minimizeDecisionModal,
      minimizeModal,
      openChecklistModal,
      openModal,
      closeModal,
      showOnboardingCallCta,
      usesLargeCompanyOnboarding,
      shouldShowIndicator,
      showCompletionIndicator,
    ]
  );

  return (
    <OnboardingExperienceContext.Provider value={value}>
      {children}
    </OnboardingExperienceContext.Provider>
  );
};

export const useOnboardingExperience = () => {
  const context = useContext(OnboardingExperienceContext);
  return context;
};
